import { Baby, Check, Clock, Gem, ShieldCheck, Sparkles, Store as StoreIcon, Tag } from 'lucide-preact';
import { CAMO_BY_ID, GADGET_BY_ID, SKIN_PRESET_BY_ID } from '@vm/content';
import type { CatalogEntry, GemPack, ItemType, StoreOffer } from '@vm/shared';
import { buyGemPack, buyItem, buyOffer } from '../state/actions';
import { itemInfo, ownsItem, RARITY_ORDER, stylePreviewPet, TYPE_LABEL } from '../state/content';
import { replace, route } from '../state/nav';
import { catalog, cosmetics, level, restricted, wallet } from '../state/store';
import { isNativeApp, platform, storeName } from '../platform';
import { fmtNum, hex } from '../ui/format';
import { useCompact } from '../ui/hooks';
import { Icon } from '../ui/icons';
import { AsyncButton, confirmDialog, Countdown, Empty, GemIcon, OnlineOnly, Price, RarityTag, Screen, SkinImg, Spinner, Tabs, WeaponImg } from '../ui/kit';
import { PetPreview } from '../ui/PetPreview';
import { ScopeSwatch } from '../ui/ScopeSwatch';
import { TracerSwatch } from '../ui/TracerSwatch';
import { BannerView } from './Wardrobe';

type Tab = 'featured' | 'gems' | 'looks';

export function ItemThumb(props: { type: ItemType; id: string; scale?: number }) {
  const c = cosmetics.value;
  const compact = useCompact();
  const petSize = compact ? 52 : (props.scale ?? 4) >= 4 ? 72 : 64;
  switch (props.type) {
    case 'skin':
      return SKIN_PRESET_BY_ID[props.id] ? <SkinImg skin={`preset:${props.id}`} armor={c.armor} scale={props.scale ?? 4} bare /> : <Icon name="sparkles" size={34} />;
    case 'armor':
      return <SkinImg skin={c.skin} armor={props.id} scale={props.scale ?? 4} />;
    case 'weapon':
      return <WeaponImg weaponId={props.id} scale={props.scale ?? 3} />;
    case 'banner':
      return <BannerView id={props.id} />;
    case 'camo': {
      const cm = CAMO_BY_ID[props.id];
      const [a, b, d] = cm?.colors ?? [0x888888, 0x666666, 0xaaaaaa];
      return <span class="camo-swatch big" style={{ background: `linear-gradient(135deg, ${hex(a)} 0 45%, ${hex(b)} 45% 70%, ${hex(d)} 70%)` }} />;
    }
    case 'tracer':
      return <TracerSwatch id={props.id} big />;
    case 'scope':
      return <ScopeSwatch id={props.id} big />;
    case 'gadget': {
      const g = GADGET_BY_ID[props.id];
      return (
        <span class="gadget-icon" style={{ '--g': hex(g?.color ?? 0x888888) } as Record<string, string>}>
          <Icon name={g?.icon ?? 'sparkles'} size={30} />
        </span>
      );
    }
    case 'deathfx':
      return (
        <span class="fx-icon">
          <Sparkles size={34} />
        </span>
      );
    case 'pet':
      return <PetPreview petId={props.id} size={petSize} />;
    case 'petstyle':
      // Shown on the pet the player brings (or the starter pet).
      return <PetPreview petId={stylePreviewPet()} styleId={props.id} size={petSize} />;
    default:
      return (
        <span class="fx-icon">
          <Tag size={30} />
        </span>
      );
  }
}

async function confirmBuy(name: string, price: number, currency: 'coins' | 'gems'): Promise<boolean> {
  return confirmDialog({
    title: `Buy ${name}?`,
    body: (
      <span class="confirm-price">
        <Price price={price} currency={currency} />
      </span>
    ),
    confirm: 'Buy',
  });
}

function OfferCard(props: { offer: StoreOffer }) {
  const o = props.offer;
  const allOwned = o.items.length > 0 && o.items.every((it) => ownsItem(it.type, it.id));
  const w = wallet.value;
  const have = o.currency === 'gems' ? w.gems : w.coins;
  const original = o.discountPct ? Math.round(o.price / (1 - o.discountPct / 100)) : null;
  return (
    <div class={`offer ${o.featured ? 'is-featured' : ''}`}>
      {!!o.discountPct && <span class="offer-discount">-{o.discountPct}%</span>}
      <div class="offer-head">
        <b>{o.name}</b>
        <span class="offer-timer">
          <Clock size={14} /> <Countdown to={o.endsAt} />
        </span>
      </div>
      <div class="offer-items">
        {o.items.map((it) => {
          const info = itemInfo(it.type, it.id);
          return (
            <div key={`${it.type}:${it.id}`} class={`offer-item r-${info.rarity}`}>
              <span class="offer-item-img">
                <ItemThumb type={it.type} id={it.id} />
              </span>
              <span class="offer-item-name">{info.name}</span>
              <small>{TYPE_LABEL[it.type]}</small>
            </div>
          );
        })}
      </div>
      <div class="offer-foot">
        {original && <s class="offer-was">{fmtNum(original)}</s>}
        {allOwned ? (
          <button class="btn" disabled>
            <Check size={18} /> Owned
          </button>
        ) : (
          <AsyncButton
            class={o.currency === 'gems' ? 'gem' : 'primary'}
            disabled={have < o.price}
            onClick={async () => {
              if (await confirmBuy(o.name, o.price, o.currency)) await buyOffer(o);
            }}
          >
            <Price price={o.price} currency={o.currency} />
          </AsyncButton>
        )}
      </div>
    </div>
  );
}

function Featured() {
  const offers = catalog.value?.offers ?? [];
  const live = offers.filter((o) => Date.parse(o.endsAt) > Date.now());
  if (!live.length) return <Empty icon={<StoreIcon size={44} />} title="New offers soon" body="The store rotates regularly. Check back later!" />;
  return (
    <div class="offer-grid">
      {[...live].sort((a, b) => Number(!!b.featured) - Number(!!a.featured)).map((o) => (
        <OfferCard key={o.id} offer={o} />
      ))}
    </div>
  );
}

function usd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function GemPackCard(props: { pack: GemPack; index: number }) {
  const p = props.pack;
  return (
    <div class={`gempack ${p.badge ? 'has-badge' : ''}`}>
      {p.badge && <span class="gempack-badge">{p.badge}</span>}
      <div class={`gempack-pile size-${Math.min(4, props.index)}`}>
        {Array.from({ length: Math.min(5, props.index + 1) }, (_, i) => (
          <GemIcon key={i} size={40 - i * 3} />
        ))}
      </div>
      <b class="gempack-amount">{fmtNum(p.gems)}</b>
      {p.bonus > 0 ? <span class="gempack-bonus">+{fmtNum(p.bonus)} bonus</span> : <span class="gempack-bonus none">&nbsp;</span>}
      <span class="gempack-name">{p.name}</span>
      <AsyncButton class="gem gempack-buy" onClick={() => buyGemPack(p)}>
        {usd(p.priceCents)}
      </AsyncButton>
    </div>
  );
}

function AskParent() {
  return (
    <div class="ask-parent panel">
      <Baby size={48} />
      <div>
        <h3>Ask a parent</h3>
        <p>
          Gems can only be bought by a parent or guardian{isNativeApp ? ` through ${storeName()}, where they can approve each purchase.` : ' through the Voxel Marines app on the App Store or Google Play.'}
        </p>
        <p class="muted">You can still earn gems for free from the Battle Pass, quests and achievements!</p>
      </div>
    </div>
  );
}

function Gems() {
  const packs = catalog.value?.gemPacks ?? [];
  const kid = restricted.value;
  const blocked = kid && !isNativeApp;
  return (
    <div class="gems-page">
      {blocked ? (
        <AskParent />
      ) : packs.length ? (
        <div class="gempack-grid">
          {[...packs]
            .sort((a, b) => a.priceCents - b.priceCents)
            .map((p, i) => (
              <GemPackCard key={p.id} pack={p} index={i} />
            ))}
        </div>
      ) : (
        <Empty icon={<Gem size={44} />} title="Gem packs unavailable" body="Please try again later." />
      )}
      <div class="parent-note">
        <ShieldCheck size={22} />
        <p>
          <b>For parents:</b> gems buy cosmetics, the premium battle pass and name changes — never weapons or power. There are no random paid loot boxes.{' '}
          {platform === 'web'
            ? 'On the web, purchases use a secure checkout; players under 13 cannot buy here.'
            : `Purchases go through ${storeName()}, so Ask to Buy and Family Link approvals apply.`}{' '}
          Prices in USD; your store may show local prices.
        </p>
      </div>
    </div>
  );
}

function LookCard(props: { entry: CatalogEntry }) {
  const e = props.entry;
  const owned = ownsItem(e.type, e.id);
  const locked = level.value < e.unlockLevel;
  const w = wallet.value;
  const have = e.currency === 'gems' ? w.gems : w.coins;
  return (
    <div class={`arm-card look r-${e.rarity}`}>
      <span class="arm-card-img">
        <ItemThumb type={e.type} id={e.id} />
      </span>
      <span class="arm-card-name">{e.name}</span>
      <span class="look-meta">
        <small>{TYPE_LABEL[e.type]}</small>
        <RarityTag rarity={e.rarity} />
      </span>
      {owned ? (
        <span class="arm-chip owned">
          <Check size={14} strokeWidth={3} /> Owned
        </span>
      ) : locked ? (
        <span class="arm-chip locked">Lv {e.unlockLevel}</span>
      ) : (
        <AsyncButton
          class={`sm ${e.currency === 'gems' ? 'gem' : 'primary'}`}
          disabled={have < e.price}
          onClick={async () => {
            if (await confirmBuy(e.name, e.price, e.currency)) await buyItem(e.type, e.id, e.name);
          }}
        >
          <Price price={e.price} currency={e.currency} />
        </AsyncButton>
      )}
    </div>
  );
}

function Looks() {
  const items = (catalog.value?.items ?? []).filter(
    (e) => (!e.source || e.source === 'shop') && e.price > 0 && ['skin', 'armor', 'camo', 'tracer', 'scope', 'deathfx', 'banner', 'title', 'petstyle'].includes(e.type),
  );
  if (!items.length) return <Empty icon={<Sparkles size={44} />} title="No looks for sale right now" />;
  const sorted = [...items].sort((a, b) => RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity) || a.price - b.price);
  return (
    <div class="arm-grid looks">
      {sorted.map((e) => (
        <LookCard key={`${e.type}:${e.id}`} entry={e} />
      ))}
    </div>
  );
}

export function StoreScreen() {
  const tab = (route.value.params.tab as Tab) || 'featured';
  return (
    <Screen
      title="Store"
      icon={<StoreIcon size={26} />}
      tabs={
        <Tabs<Tab>
          value={tab}
          onChange={(t) => replace('store', { tab: t })}
          tabs={[
            { id: 'featured', label: 'Featured' },
            { id: 'gems', label: 'Gems', icon: <GemIcon size={16} /> },
            { id: 'looks', label: 'Looks' },
          ]}
        />
      }
    >
      <OnlineOnly feature="The store">
        <div class="scr-scroll" key={tab}>
          {!catalog.value ? <Spinner label="Opening the store…" /> : tab === 'featured' ? <Featured /> : tab === 'gems' ? <Gems /> : <Looks />}
        </div>
      </OnlineOnly>
    </Screen>
  );
}
